import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Network, Users, UserCircle, Building2, Plus, Upload, Search,
  FileText, Loader2, ChevronDown, ChevronRight,
  Shield, Briefcase, GraduationCap, Settings, BarChart3, X
} from "lucide-react";
import { FALLBACK_ORG_LEVELS, FALLBACK_ORG_POSITIONS, FALLBACK_ORG_MEMBERS } from "@/data/dummyData";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function HumanCentre() {
  // Organization state
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [members, setMembers] = useState([]);
  const [organogram, setOrganogram] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selected item state
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Dialog states
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showLevelSettings, setShowLevelSettings] = useState(false);
  
  // Form states
  const [newPosition, setNewPosition] = useState({
    title: "", level_code: "", department: "", reports_to: "",
    job_profile: "", skills_required: "", qualifications_required: ""
  });
  const [newMember, setNewMember] = useState({
    name: "", email: "", employee_id: "", position_id: "",
    department: "", level_code: "", skills: "", qualifications: ""
  });
  
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Expanded levels in organogram
  const [expandedLevels, setExpandedLevels] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [levelsRes, positionsRes, membersRes, organogramRes] = await Promise.all([
        axios.get(`${API}/api/hr/organization/levels`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/positions`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/members`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/organogram`, { headers: getHeaders(), withCredentials: true })
      ]);
      
      setLevels(levelsRes.data.length > 0 ? levelsRes.data : FALLBACK_ORG_LEVELS);
      setPositions(positionsRes.data.length > 0 ? positionsRes.data : FALLBACK_ORG_POSITIONS);
      setMembers(membersRes.data.length > 0 ? membersRes.data : FALLBACK_ORG_MEMBERS);
      setOrganogram(organogramRes.data);

      // Expand all levels by default
      const expanded = {};
      levelsRes.data.forEach(l => { expanded[l.code] = true; });
      setExpandedLevels(expanded);
    } catch (err) {
      setLevels(FALLBACK_ORG_LEVELS);
      setPositions(FALLBACK_ORG_POSITIONS);
      setMembers(FALLBACK_ORG_MEMBERS);
      toast.error("Failed to load organization data. Showing demo data.");
    }
    setIsLoading(false);
  };

  const handleAddPosition = async () => {
    try {
      const data = {
        ...newPosition,
        skills_required: newPosition.skills_required.split(",").map(s => s.trim()).filter(Boolean),
        qualifications_required: newPosition.qualifications_required.split(",").map(s => s.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/api/hr/positions`, data, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      
      toast.success("Position created");
      setShowAddPosition(false);
      setNewPosition({ title: "", level_code: "", department: "", reports_to: "", job_profile: "", skills_required: "", qualifications_required: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create position");
    }
  };

  const handleAddMember = async () => {
    try {
      const data = {
        ...newMember,
        skills: newMember.skills.split(",").map(s => s.trim()).filter(Boolean),
        qualifications: newMember.qualifications.split(",").map(s => s.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/api/hr/members`, data, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      
      toast.success("Member added");
      setShowAddMember(false);
      setNewMember({ name: "", email: "", employee_id: "", position_id: "", department: "", level_code: "", skills: "", qualifications: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    }
  };

  const toggleLevel = (levelCode) => {
    setExpandedLevels(prev => ({
      ...prev,
      [levelCode]: !prev[levelCode]
    }));
  };

  const filteredPositions = positions.filter(p => 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPositionsByLevel = (levelCode) => {
    return organogram?.positions?.filter(p => p.level_code === levelCode) || [];
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Network className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
                HUMAN CENTRE
              </h1>
              <p className="text-slate-600">Organization Structure & Member Profiles</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Dialog open={showLevelSettings} onOpenChange={setShowLevelSettings}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Levels
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Organization Levels</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[400px] overflow-auto">
                  {levels.map((level, idx) => (
                    <div key={level.id} className="flex items-center gap-3 p-3 bg-app border">
                      <span className="w-8 h-8 bg-emerald-500 text-white rounded flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium">{level.name}</p>
                        <p className="text-xs text-slate-500">Code: {level.code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
            
            <Button onClick={() => setShowAddPosition(true)} className="rounded-none bg-emerald-600 hover:bg-emerald-500">
              <Plus className="w-4 h-4 mr-2" />
              Add Position
            </Button>
            
            <Button onClick={() => setShowAddMember(true)} variant="outline" className="rounded-none">
              <UserCircle className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Organogram View */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs defaultValue="organogram">
            <TabsList className="bg-slate-100 rounded-none">
              <TabsTrigger value="organogram" className="rounded-none" data-testid="tab-organogram">
                <Network className="w-4 h-4 mr-2" />
                Organogram
              </TabsTrigger>
              <TabsTrigger value="positions" className="rounded-none" data-testid="tab-positions">
                <Briefcase className="w-4 h-4 mr-2" />
                Positions
              </TabsTrigger>
              <TabsTrigger value="members" className="rounded-none" data-testid="tab-members">
                <Users className="w-4 h-4 mr-2" />
                Members
              </TabsTrigger>
              <TabsTrigger value="reports" className="rounded-none" data-testid="tab-reports">
                <BarChart3 className="w-4 h-4 mr-2" />
                Reports
              </TabsTrigger>
            </TabsList>

            {/* Organogram Tab */}
            <TabsContent value="organogram">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Organization Structure</h3>
                  <div className="relative w-64">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search positions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 rounded-none"
                    />
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-2">
                      {levels.map((level) => {
                        const levelPositions = getPositionsByLevel(level.code);
                        const isExpanded = expandedLevels[level.code];
                        
                        return (
                          <div key={level.id} className="border border-slate-200">
                            {/* Level Header */}
                            <button
                              onClick={() => toggleLevel(level.code)}
                              className="w-full flex items-center justify-between p-3 bg-app hover:bg-slate-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-slate-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                                )}
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium">
                                  {level.code}
                                </span>
                                <span className="font-medium text-slate-700">{level.name}</span>
                              </div>
                              <span className="text-sm text-slate-500">
                                {levelPositions.length} position(s)
                              </span>
                            </button>
                            
                            {/* Positions */}
                            {isExpanded && levelPositions.length > 0 && (
                              <div className="p-3 space-y-2">
                                {levelPositions.map((position) => (
                                  <div
                                    key={position.id}
                                    className={`p-3 border rounded cursor-pointer transition-colors ${
                                      selectedPosition?.id === position.id
                                        ? 'border-emerald-500 bg-emerald-50'
                                        : 'border-slate-200 hover:border-emerald-300'
                                    }`}
                                    onClick={() => setSelectedPosition(position)}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <p className="font-medium text-slate-800">{position.title}</p>
                                        <p className="text-sm text-slate-500">{position.department}</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {position.occupants?.length > 0 ? (
                                          <div className="flex items-center gap-1">
                                            <UserCircle className="w-4 h-4 text-emerald-500" />
                                            <span className="text-sm text-emerald-600">
                                              {position.occupants.length} occupant(s)
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-orange-500">Vacant</span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Occupants */}
                                    {position.occupants?.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-slate-100">
                                        {position.occupants.map((occ) => (
                                          <div
                                            key={occ.id}
                                            className="flex items-center gap-2 text-sm text-slate-600 py-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedMember(members.find(m => m.id === occ.id));
                                            }}
                                          >
                                            <UserCircle className="w-4 h-4" />
                                            <span className="hover:text-emerald-600 cursor-pointer">{occ.name}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {isExpanded && levelPositions.length === 0 && (
                              <div className="p-4 text-center text-slate-500 text-sm">
                                No positions at this level
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </TabsContent>

            {/* Positions Tab */}
            <TabsContent value="positions">
              <div className="bg-white border border-slate-200 p-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredPositions.map((position) => (
                    <div
                      key={position.id}
                      className="p-4 border border-slate-200 hover:border-emerald-400 cursor-pointer"
                      onClick={() => setSelectedPosition(position)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium text-slate-800">{position.title}</span>
                      </div>
                      <p className="text-sm text-slate-500">{position.department}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs">
                          {position.level_code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members">
              <div className="bg-white border border-slate-200 p-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="p-4 border border-slate-200 hover:border-emerald-400 cursor-pointer"
                      onClick={() => setSelectedMember(member)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <UserCircle className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.department || member.level_code}</p>
                        </div>
                      </div>
                      {member.limited_view && (
                        <p className="text-xs text-orange-500 mt-2">Limited view</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <ReportsSection />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Details & Bot */}
        <div className="space-y-4">
          {/* Position/Member Details */}
          {(selectedPosition || selectedMember) && (
            <div className="bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">
                  {selectedPosition ? "Position Details" : "Member Profile"}
                </h3>
                <button
                  onClick={() => { setSelectedPosition(null); setSelectedMember(null); }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {selectedPosition && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Title</p>
                    <p className="font-medium text-slate-800">{selectedPosition.title}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Level</p>
                    <p className="text-slate-700">{selectedPosition.level_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Department</p>
                    <p className="text-slate-700">{selectedPosition.department || "N/A"}</p>
                  </div>
                  {selectedPosition.job_profile && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Job Profile</p>
                      <p className="text-sm text-slate-700">{selectedPosition.job_profile}</p>
                    </div>
                  )}
                  {selectedPosition.skills_required?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Skills Required</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedPosition.skills_required.map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedPosition.qualifications_required?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Qualifications Required</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedPosition.qualifications_required.map((qual, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                            {qual}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {selectedMember && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <UserCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{selectedMember.name}</p>
                      <p className="text-sm text-slate-500">{selectedMember.email}</p>
                    </div>
                  </div>
                  
                  {selectedMember.limited_view ? (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-sm text-orange-700">
                        <Shield className="w-4 h-4 inline mr-1" />
                        You can only view basic information for this member.
                      </p>
                    </div>
                  ) : (
                    <>
                      {selectedMember.skills?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Skills</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedMember.skills.map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedMember.qualifications?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Qualifications</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedMember.qualifications.map((qual, i) => (
                              <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                {qual}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

        {/* Add Position Dialog */}
      <Dialog open={showAddPosition} onOpenChange={setShowAddPosition}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Position</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Position Title</Label>
              <Input
                value={newPosition.title}
                onChange={(e) => setNewPosition(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Senior Analyst"
                className="rounded-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Level</Label>
                <Select value={newPosition.level_code} onValueChange={(v) => setNewPosition(p => ({ ...p, level_code: v }))}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {levels.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={newPosition.department}
                  onChange={(e) => setNewPosition(p => ({ ...p, department: e.target.value }))}
                  placeholder="e.g. Finance"
                  className="rounded-none"
                />
              </div>
            </div>
            <div>
              <Label>Job Profile</Label>
              <Textarea
                value={newPosition.job_profile}
                onChange={(e) => setNewPosition(p => ({ ...p, job_profile: e.target.value }))}
                placeholder="Describe the role..."
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Skills Required (comma-separated)</Label>
              <Input
                value={newPosition.skills_required}
                onChange={(e) => setNewPosition(p => ({ ...p, skills_required: e.target.value }))}
                placeholder="e.g. Excel, Data Analysis, Python"
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Qualifications Required (comma-separated)</Label>
              <Input
                value={newPosition.qualifications_required}
                onChange={(e) => setNewPosition(p => ({ ...p, qualifications_required: e.target.value }))}
                placeholder="e.g. Bachelor's Degree, CPA"
                className="rounded-none"
              />
            </div>
            <Button onClick={handleAddPosition} className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500">
              Create Position
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={newMember.name}
                  onChange={(e) => setNewMember(m => ({ ...m, name: e.target.value }))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember(m => ({ ...m, email: e.target.value }))}
                  className="rounded-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID</Label>
                <Input
                  value={newMember.employee_id}
                  onChange={(e) => setNewMember(m => ({ ...m, employee_id: e.target.value }))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label>Position</Label>
                <Select value={newMember.position_id} onValueChange={(v) => setNewMember(m => ({ ...m, position_id: v }))}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Skills (comma-separated)</Label>
              <Input
                value={newMember.skills}
                onChange={(e) => setNewMember(m => ({ ...m, skills: e.target.value }))}
                placeholder="e.g. Excel, Python, SQL"
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Qualifications (comma-separated)</Label>
              <Input
                value={newMember.qualifications}
                onChange={(e) => setNewMember(m => ({ ...m, qualifications: e.target.value }))}
                placeholder="e.g. MBA, PMP"
                className="rounded-none"
              />
            </div>
            <Button onClick={handleAddMember} className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500">
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reports Section Component
function ReportsSection() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);

  const generateReport = async (type) => {
    setIsGenerating(true);
    setSelectedReport(type);
    setReportData(null);

    try {
      const endpoints = {
        "skills-audit": "/api/hr/reports/skills-audit",
        "demographics": "/api/hr/reports/demographics",
        "development": "/api/hr/reports/development"
      };

      const res = await axios.get(`${API}${endpoints[type]}`, {
        headers: getHeaders(),
        withCredentials: true
      });

      setReportData(res.data);
      toast.success("Report generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    }
    setIsGenerating(false);
  };

  return (
    <div className="bg-white border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-800 mb-4">HR Reports (AI-Powered)</h3>
      
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { id: "skills-audit", label: "Skills Audit", icon: GraduationCap, color: "blue" },
          { id: "demographics", label: "Demographics", icon: Users, color: "emerald" },
          { id: "development", label: "Development Needs", icon: BarChart3, color: "purple" }
        ].map((report) => (
          <Button
            key={report.id}
            onClick={() => generateReport(report.id)}
            disabled={isGenerating}
            variant={selectedReport === report.id ? "default" : "outline"}
            className={`h-24 flex-col rounded-none ${
              selectedReport === report.id ? `bg-${report.color}-600` : ""
            }`}
          >
            <report.icon className="w-6 h-6 mb-2" />
            {report.label}
          </Button>
        ))}
      </div>

      {isGenerating && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mr-3" />
          <span className="text-slate-600">Generating AI report...</span>
        </div>
      )}

      {reportData && !isGenerating && (
        <div className="border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-slate-800">{reportData.report_type}</h4>
            <span className="text-xs text-slate-500">
              Generated: {new Date(reportData.generated_at).toLocaleString()}
            </span>
          </div>
          <ScrollArea className="h-[400px]">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-['IBM_Plex_Sans']">
              {reportData.analysis}
            </pre>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
